import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  ButtonGroup,
  Col,
  Dropdown,
  Form,
  Row,
  Modal,
} from "react-bootstrap";
import { BsPersonPlusFill } from "react-icons/bs";
import {
  FaEdit,
  FaUsers,
  FaCheckCircle,
  FaWpforms,
  FaDownload,
  FaPrint,
} from "react-icons/fa";
import { MdAccessTime } from "react-icons/md";
import { IoEye } from "react-icons/io5";
import { TbReplaceFilled } from "react-icons/tb";
import { MdDelete } from "react-icons/md";
import axios from "axios";
import AtdLayout from "../../components/AttendanceLayout.jsx";
import "./yesterday-atd.css";

export default function YesterdayAttendance() {
  const rowsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [earlyCloseDocs, setEarlyCloseDocs] = useState({});
  const [overtimeDocs, setOvertimeDocs] = useState({});
  const [lateDocs, setLateDocs] = useState({});
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedDocUrl, setSelectedDocUrl] = useState("");
  const [attendanceId, setAttendanceId] = useState(null);
  const [docCategory, setDocCategory] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showEarlyLeaveApprovalModal, setShowEarlyLeaveApprovalModal] =
    useState(false);
  const [showLateApprovalModal, setShowLateApprovalModal] = useState(false);
  const [showOvertimeApprovalModal, setShowOvertimeApprovalModal] =
    useState(false);
  const [approvalData, setApprovalData] = useState({});
  const [hrSignature, setHrSignature] = useState("");
  const [adminSignature, setAdminSignature] = useState("");
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [applySingleDeduction, setApplySingleDeduction] = useState(false);
  const fileInputRef = useRef(null);
  const printRef = useRef(null);

  // Function to fetch attendance data and handle docs
  const fetchAttendance = () => {
    setLoading(true);
    fetch(
      `https://backend.ems.hiradentallab.com/api/attendance/attendance/yesterday?search=${searchQuery}`
    )
      .then((response) => response.json())
      .then((fetchedData) => {
        const fetchedDataArr = fetchedData.data || [];
        const processedData = fetchedDataArr.map((item) => ({
          ...item,
          lateReason: item.lateReason ?? "Not provided",
          earlyCloseReason: item.earlyCloseReason ?? "Not provided",
          overtimeReason: item.earlyCloseReason ?? "Not provided",
          lateSingleDeduction: item.lateSingleDeduction ?? false,
          earlyCloseSingleDeduction: item.earlyCloseSingleDeduction ?? false,
          lateHrSignature: item.lateHrSignature ?? null,
          lateAdminSignature: item.lateAdminSignature ?? null,
          earlyCloseHrSignature: item.earlyCloseHrSignature ?? null,
          earlyCloseAdminSignature: item.earlyCloseAdminSignature ?? null,
          overtimeHrSignature: item.overtimeHrSignature ?? null,
          overtimeAdminSignature: item.overtimeAdminSignature ?? null,
        }));
        const newEarlyCloseDocs = {};
        const newOvertimeDocs = {};
        const newLateDocs = {};
        fetchedDataArr.forEach((item) => {
          if (item.earlyCloseDocs)
            newEarlyCloseDocs[item._id] = item.earlyCloseDocs;
          if (item.overtimeDocs) newOvertimeDocs[item._id] = item.overtimeDocs;
          if (item.lateDocs) newLateDocs[item._id] = item.lateDocs;
        });
        setData(processedData);
        setFilteredData(processedData);
        setEarlyCloseDocs(newEarlyCloseDocs);
        setOvertimeDocs(newOvertimeDocs);
        setLateDocs(newLateDocs);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching attendance data:", error);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchAttendance();
  }, [searchQuery]);

  const handleShowModal = (data) => {
    setModalData(data);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setModalData({});
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchAttendance();
  };

  const handleViewDocument = (docUrl, item, category) => {
    if (!docUrl || !item || !item._id) {
      console.error("Invalid document or item:", { docUrl, item });
      alert("Something went wrong. Document or attendance data missing.");
      return;
    }

    let formattedUrl = docUrl.startsWith("http")
      ? docUrl
      : `http://localhost:5544${
          docUrl.startsWith("/uploads") ? docUrl : `/uploads/${docUrl}`
        }`;

    setSelectedDocUrl(formattedUrl);
    setDocCategory(category);
    setShowViewModal(true);
    setAttendanceId(item._id);
  };

  const handleDownload = async () => {
    try {
      const response = await fetch(selectedDocUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const filename = selectedDocUrl.split("/").pop() || "document";
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
      alert("Download failed. Please try again.");
    }
  };

  const handleFileReplace = async (e) => {
    const file = e.target.files[0];
    if (!file || !attendanceId || !docCategory) {
      alert(
        "No file selected, attendance ID missing, or document category not specified."
      );
      return;
    }

    const formData = new FormData();
    formData.append("document", file);
    formData.append("uploadCategory", docCategory);

    try {
      const response = await fetch(
        `https://backend.ems.hiradentallab.com/api/attendance/update-document/${attendanceId}`,
        {
          method: "POST",
          body: formData,
        }
      );
      const data = await response.json();

      if (response.ok) {
        alert("Document replaced successfully!");
        setShowViewModal(false);
        fetchAttendance();
      } else {
        alert(data.message || "Failed to replace document");
      }
    } catch (error) {
      console.error("Error replacing file:", error);
      alert("Error replacing file.");
    }
  };

  const resetApprovalData = () => {
    setApprovalData({});
    setHrSignature("");
    setAdminSignature("");
    setIsViewOnly(false);
    setApplySingleDeduction(false);
  };

  const handleOpenLateApprovalForm = (item) => {
    resetApprovalData();
    const currentRole = localStorage.getItem("role");
    const hrHasApproved = !!item.lateHrSignature;
    const adminHasApproved = !!item.lateAdminSignature;

    const canEditHRSignature = currentRole === "HR" && !hrHasApproved;
    const canEditAdminSignature = currentRole === "admin" && !adminHasApproved;

    setApprovalData({
      userName: item.userName,
      userId: item.userId,
      date: new Date(item.checkIn).toLocaleDateString(),
      department: item.department || "Not specified",
      type: "Late Arrival",
      minutes: item.lateMinutes,
      reason: item.lateReason || "Not provided",
      attendanceId: item._id,
      singleDeduction: item.lateSingleDeduction || false,
      hrHasApproved,
      adminHasApproved,
      canEditHRSignature,
      canEditAdminSignature,
      currentRole,
    });

    setHrSignature(item.lateHrSignature || "");
    setAdminSignature(item.lateAdminSignature || "");
    setIsViewOnly(!canEditHRSignature && !canEditAdminSignature);
    setApplySingleDeduction(item.lateSingleDeduction || false);
    setShowLateApprovalModal(true);
  };

  const handleOpenEarlyLeaveApprovalForm = (item) => {
    resetApprovalData();
    const currentRole = localStorage.getItem("role");
    const hrHasApproved = !!item.earlyCloseHrSignature;
    const adminHasApproved = !!item.earlyCloseAdminSignature;

    const canEditHRSignature = currentRole === "HR" && !hrHasApproved;
    const canEditAdminSignature = currentRole === "admin" && !adminHasApproved;

    setApprovalData({
      userName: item.userName,
      userId: item.userId,
      date: new Date(item.checkIn).toLocaleDateString(),
      department: item.department || "Not specified",
      type: "Early Leave",
      minutes: item.earlyCloseMinutes,
      reason: item.earlyCloseReason || "Not provided",
      attendanceId: item._id,
      singleDeduction: item.earlyCloseSingleDeduction || false,
      hrHasApproved,
      adminHasApproved,
      canEditHRSignature,
      canEditAdminSignature,
      currentRole,
    });

    setHrSignature(item.earlyCloseHrSignature || "");
    setAdminSignature(item.earlyCloseAdminSignature || "");
    setIsViewOnly(!canEditHRSignature && !canEditAdminSignature);
    setApplySingleDeduction(item.earlyCloseSingleDeduction || false);
    setShowEarlyLeaveApprovalModal(true);
  };

  const handleOpenOvertimeApprovalForm = (item) => {
    resetApprovalData();
    const currentRole = localStorage.getItem("role");
    const hrHasApproved = !!item.overtimeHrSignature;
    const adminHasApproved = !!item.overtimeAdminSignature;

    const canEditHRSignature = currentRole === "HR" && !hrHasApproved;
    const canEditAdminSignature = currentRole === "admin" && !adminHasApproved;

    setApprovalData({
      userName: item.userName,
      userId: item.userId,
      date: new Date(item.checkIn).toLocaleDateString(),
      department: item.department || "Not specified",
      type: "Overtime",
      minutes: item.extraMinutes,
      reason: item.overtimeReason || "Not provided",
      attendanceId: item._id,
      hrHasApproved,
      adminHasApproved,
      canEditHRSignature,
      canEditAdminSignature,
      currentRole,
    });

    setHrSignature(item.overtimeHrSignature || "");
    setAdminSignature(item.overtimeAdminSignature || "");
    setIsViewOnly(!canEditHRSignature && !canEditAdminSignature);
    setApplySingleDeduction(false);
    setShowOvertimeApprovalModal(true);
  };

  const handleApprove = async (type) => {
    const role = localStorage.getItem("role");
    if (!role || (role !== "HR" && role !== "admin")) {
      alert(
        "You do not have permission to approve this request. Please log in as HR or Admin."
      );
      return;
    }

    if (role === "HR" && !hrSignature) {
      alert("Please provide an HR signature before approving.");
      return;
    }
    if (role === "admin" && !adminSignature) {
      alert("Please provide an Admin signature before approving.");
      return;
    }

    try {
      const updateData = {
        type,
        approve: role === "admin" ? true : undefined,
        semiApproved: role === "HR" ? true : undefined,
        deduct:
          type !== "overtime" && role === "admin"
            ? !applySingleDeduction
            : undefined,
        singleDeduction:
          type !== "overtime" && role === "admin"
            ? applySingleDeduction
            : undefined,
        hrSignature: role === "HR" ? hrSignature : undefined,
        adminSignature: role === "admin" ? adminSignature : undefined,
      };

      const response = await axios.put(
        `https://backend.ems.hiradentallab.com/api/attendance/${approvalData.attendanceId}/deduct`,
        updateData,
        {
          headers: {
            "x-user-role": role,
          },
        }
      );

      if (response.status === 200) {
        alert(
          `Attendance ${type} ${
            role === "admin" ? "approved" : "semi-approved"
          } successfully!`
        );
        const signatureFields = {
          late: {
            hrSignatureField: "lateHrSignature",
            adminSignatureField: "lateAdminSignature",
            minutesField: "lateMinutes",
            singleDeductionField: "lateSingleDeduction",
            deductedField: "lateDeducted",
          },
          earlyClose: {
            hrSignatureField: "earlyCloseHrSignature",
            adminSignatureField: "earlyCloseAdminSignature",
            minutesField: "earlyCloseMinutes",
            singleDeductionField: "earlyCloseSingleDeduction",
            deductedField: "earlyCloseDeducted",
          },
          overtime: {
            hrSignatureField: "overtimeHrSignature",
            adminSignatureField: "overtimeAdminSignature",
          },
        };
        const updateState = (prev) =>
          prev.map((item) =>
            item._id === approvalData.attendanceId
              ? {
                  ...item,
                  [`${type}Approved`]:
                    role === "admin" ? true : item[`${type}Approved`],
                  [`${type}SemiApproved`]:
                    role === "HR" ? true : item[`${type}SemiApproved`],
                  ...(type !== "overtime" && role === "admin"
                    ? {
                        [signatureFields[type].deductedField]:
                          !applySingleDeduction,
                        [signatureFields[type].singleDeductionField]:
                          applySingleDeduction,
                        [signatureFields[type].minutesField]:
                          response.data.data[
                            signatureFields[type].minutesField
                          ],
                      }
                    : {}),
                  [signatureFields[type].hrSignatureField]:
                    role === "HR"
                      ? hrSignature
                      : item[signatureFields[type].hrSignatureField],
                  [signatureFields[type].adminSignatureField]:
                    role === "admin"
                      ? adminSignature
                      : item[signatureFields[type].adminSignatureField],
                }
              : item
          );
        setData(updateState);
        setFilteredData(updateState);
        if (type === "late") setShowLateApprovalModal(false);
        if (type === "earlyClose") setShowEarlyLeaveApprovalModal(false);
        if (type === "overtime") setShowOvertimeApprovalModal(false);
        resetApprovalData();
      } else {
        alert("Failed to approve attendance record.");
      }
    } catch (error) {
      console.error(
        "Error approving attendance:",
        error.response?.data || error
      );
      alert(
        "Error approving attendance record: " +
          (error.response?.data?.message || error.message)
      );
    }
  };

  const handlePrint = () => {
    const printContent = printRef.current.innerHTML;
    const originalContent = document.body.innerHTML;
    document.body.innerHTML = printContent;
    window.print();
    document.body.innerHTML = originalContent;
    window.location.reload();
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      const response = await axios.post(
        "https://backend.ems.hiradentallab.com/api/attendance/attendance/delete",
        {
          attendanceId: itemToDelete._id,
        }
      );

      if (response.data.success) {
        alert("Attendance record deleted successfully!");
        fetchAttendance();
      } else {
        alert(response.data.message || "Failed to delete attendance record");
      }
    } catch (error) {
      console.error("Error deleting attendance:", error);
      alert("Error deleting attendance record. Please try again.");
    } finally {
      setShowDeleteModal(false);
      setItemToDelete(null);
    }
  };

  const totalPages = Math.ceil(data.length / rowsPerPage);
  const paginatedData = data.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const pageRange = () => {
    const pages = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(startPage + 4, totalPages);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  const role = localStorage.getItem("role");

  // Helper function to determine button variant based on approval status
  const getApprovalButtonVariant = (item, type) => {
    if (type === "late") {
      return item.lateApproved ? "success" : "danger";
    } else if (type === "earlyClose") {
      return item.earlyCloseApproved ? "success" : "danger";
    } else if (type === "overtime") {
      return item.overtimeApproved ? "success" : "danger";
    }
    return "danger";
  };

  // Helper function to determine button icon based on approval status
  const getApprovalButtonIcon = (item, type) => {
    if (type === "late") {
      return item.lateApproved ? <FaCheckCircle /> : <FaWpforms />;
    } else if (type === "earlyClose") {
      return item.earlyCloseApproved ? <FaCheckCircle /> : <FaWpforms />;
    } else if (type === "overtime") {
      return item.overtimeApproved ? <FaCheckCircle /> : <FaWpforms />;
    }
    return <FaWpforms />;
  };

  return (
    <AtdLayout>
      <div>
        <h1 className="yesterday-attendance-title">
          <FaUsers className="settings-icon" /> Employees
        </h1>
        <hr />
        <div className="yesterday-attendance-content">
          <Row>
            <Col xs="auto">
              <Link to="/add-atd">
                <Button variant="primary" style={{ marginBottom: "10px" }}>
                  <BsPersonPlusFill /> Add Attendance
                </Button>
              </Link>
            </Col>
          </Row>
          <div className="yesterday-attendance-actions">
            <Row>
              <Col xs={12} md={6} className="d-flex align-items-center">
                <Dropdown as={ButtonGroup}>
                  <Button variant="primary">Show Entries</Button>
                  <Dropdown.Toggle
                    split
                    variant="primary"
                    id="dropdown-split-basic"
                  />
                  <Dropdown.Menu>
                    <Dropdown.Item as="button">10</Dropdown.Item>
                    <Dropdown.Item as="button">25</Dropdown.Item>
                    <Dropdown.Item as="button">50</Dropdown.Item>
                    <Dropdown.Item as="button">100</Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </Col>
              <Col xs={12} md={6} className="d-flex justify-content-end">
                <div className="search-container">
                  <Form className="d-flex" onSubmit={handleSearchSubmit}>
                    <Form.Control
                      type="search"
                      placeholder="Search"
                      aria-label="Search"
                      className="hr-info-search"
                      value={searchQuery}
                      onChange={handleSearchChange}
                    />
                  </Form>
                </div>
              </Col>
            </Row>
          </div>
          <div className="table-responsive">
            {loading ? (
              <p>Loading...</p>
            ) : (
              <table className="table table-striped table-style">
                <thead className="yesterday-attendance-table-header">
                  <tr className="table-header">
                    <th>SR</th>
                    <th>UserId</th>
                    <th>Employee Name</th>
                    <th>CheckIn</th>
                    <th>CheckOut</th>
                    <th>Worked Hours</th>
                    <th>Late Absent Minutes</th>
                    <th>Early Absent Minutes</th>
                    <th>Overtime</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData
                    .slice(
                      (currentPage - 1) * rowsPerPage,
                      currentPage * rowsPerPage
                    )
                    .map((item, index) => (
                      <tr key={index}>
                        <td>{(currentPage - 1) * rowsPerPage + index + 1}</td>
                        <td>{item.userId}</td>
                        <td>{item.userName}</td>
                        <td>
                          {new Date(item.checkIn).toLocaleTimeString("en-US", {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          })}
                        </td>
                        <td>
                          {item.checkOut
                            ? new Date(item.checkOut).toLocaleTimeString(
                                "en-US",
                                {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                }
                              )
                            : "At Work"}
                        </td>
                        <td>{item.workedHours}</td>
                        <td>
                          {item.lateMinutes || "0"}{" "}
                          {lateDocs[item._id] && (
                            <Button
                              style={{
                                color: "#fff",
                                textDecoration: "none",
                                fontWeight: "500",
                                padding: "4px 8px",
                                backgroundColor: "#17a2b8",
                                borderRadius: "4px",
                                cursor: "pointer",
                                marginLeft: "9px",
                                border: "none",
                              }}
                              onClick={() =>
                                handleViewDocument(
                                  lateDocs[item._id],
                                  item,
                                  "late"
                                )
                              }
                            >
                              <IoEye />
                            </Button>
                          )}
                          {item.lateMinutes > 0 && (
                            <Button
                              variant={getApprovalButtonVariant(item, "late")}
                              size="sm"
                              style={{ marginLeft: "5px" }}
                              onClick={() => handleOpenLateApprovalForm(item)}
                            >
                              {getApprovalButtonIcon(item, "late")}
                            </Button>
                          )}
                        </td>
                        <td>
                          {item.earlyCloseMinutes || "0"}{" "}
                          {earlyCloseDocs[item._id] && (
                            <Button
                              style={{
                                color: "#fff",
                                textDecoration: "none",
                                fontWeight: "500",
                                padding: "4px 8px",
                                backgroundColor: "#17a2b8",
                                borderRadius: "4px",
                                cursor: "pointer",
                                marginLeft: "9px",
                                border: "none",
                              }}
                              onClick={() =>
                                handleViewDocument(
                                  earlyCloseDocs[item._id],
                                  item,
                                  "earlyClose"
                                )
                              }
                            >
                              <IoEye />
                            </Button>
                          )}
                          {item.earlyCloseMinutes > 0 && (
                            <Button
                              variant={getApprovalButtonVariant(
                                item,
                                "earlyClose"
                              )}
                              size="sm"
                              style={{ marginLeft: "5px" }}
                              onClick={() =>
                                handleOpenEarlyLeaveApprovalForm(item)
                              }
                            >
                              {getApprovalButtonIcon(item, "earlyClose")}
                            </Button>
                          )}
                        </td>
                        <td>
                          {item.extraMinutes || "0"}{" "}
                          {overtimeDocs[item._id] && (
                            <Button
                              style={{
                                color: "#fff",
                                textDecoration: "none",
                                fontWeight: "500",
                                padding: "4px 8px",
                                backgroundColor: "#17a2b8",
                                borderRadius: "4px",
                                cursor: "pointer",
                                marginLeft: "9px",
                                border: "none",
                              }}
                              onClick={() =>
                                handleViewDocument(
                                  overtimeDocs[item._id],
                                  item,
                                  "overtime"
                                )
                              }
                            >
                              <IoEye />
                            </Button>
                          )}
                          {item.extraMinutes > 0 && (
                            <Button
                              variant={getApprovalButtonVariant(
                                item,
                                "overtime"
                              )}
                              size="sm"
                              style={{ marginLeft: "5px" }}
                              onClick={() =>
                                handleOpenOvertimeApprovalForm(item)
                              }
                            >
                              {getApprovalButtonIcon(item, "overtime")}
                            </Button>
                          )}
                        </td>
                        <td>
                          <div>
                            <Link to={`/edit-atd/${item._id}`}>
                              <Button
                                variant="warning"
                                size="sm"
                                style={{ marginRight: "5px" }}
                              >
                                <FaEdit />
                              </Button>
                            </Link>
                            <Button
                              variant="success"
                              size="sm"
                              onClick={() => handleShowModal(item)}
                            >
                              <MdAccessTime />
                            </Button>
                            <Button
                              size="sm"
                              variant="danger"
                              onClick={() => handleDeleteClick(item)}
                              style={{ marginLeft: "5px" }}
                            >
                              <MdDelete />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
                <tfoot className="current-date-table-footer">
                  <tr className="table-footer">
                    <td>Total</td>
                    <td></td>
                    <td>{paginatedData.length}</td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
          <div className="pagination-row">
            <div className="entries-info">
              Showing {(currentPage - 1) * rowsPerPage + 1} to{" "}
              {Math.min(currentPage * rowsPerPage, data.length)} of{" "}
              {data.length} entries
            </div>
            <nav>
              <ul className="pagination no-gap">
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <a
                    href="/#"
                    className="page-link"
                    onClick={(e) => {
                      e.preventDefault();
                      handlePageChange(currentPage - 1);
                    }}
                  >
                    Previous
                  </a>
                </li>
                {pageRange().map((pageNumber) => (
                  <li
                    key={pageNumber}
                    className={`page-item ${
                      currentPage === pageNumber ? "active" : ""
                    }`}
                  >
                    <a
                      href="/#"
                      className="page-link"
                      onClick={(e) => {
                        e.preventDefault();
                        handlePageChange(pageNumber);
                      }}
                    >
                      {pageNumber}
                    </a>
                  </li>
                ))}
                <li
                  className={`page-item ${
                    currentPage === totalPages ? "disabled" : ""
                  }`}
                >
                  <a
                    href="/#"
                    className="page-link"
                    onClick={(e) => {
                      e.preventDefault();
                      handlePageChange(currentPage + 1);
                    }}
                  >
                    Next
                  </a>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
      <Modal
        show={showModal}
        onHide={handleCloseModal}
        centered
        size="lg"
        dialogClassName="fancy-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Attendance Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            <strong>UserId:</strong> {modalData.userId}
          </p>
          <p>
            <strong>Name:</strong> {modalData.userName}
          </p>
          <p>
            <strong>CheckIn:</strong>{" "}
            {modalData.checkIn
              ? new Date(modalData.checkIn).toLocaleString()
              : "N/A"}
          </p>
          <p>
            <strong>CheckOut:</strong>{" "}
            {modalData.checkOut
              ? new Date(modalData.checkOut).toLocaleString()
              : "N/A"}
          </p>
          <p>
            <strong>Worked Hours:</strong> {modalData.workedHours}
          </p>
          <p>
            <strong>Late Reason:</strong>{" "}
            {modalData.lateReason || "Not provided"}
          </p>
          <p>
            <strong>Early Close Reason:</strong>{" "}
            {modalData.earlyCloseReason || "Not provided"}
          </p>
          <p>
            <strong>Overtime Reason:</strong>{" "}
            {modalData.overtimeReason || "Not provided"}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <span>View Document</span>
            <div style={{ display: "flex", gap: "10px" }}>
              {selectedDocUrl && (
                <Button
                  variant="outline-success"
                  style={{
                    borderRadius: "10px",
                    padding: "6px 16px",
                    fontWeight: "bold",
                    background: "linear-gradient(to right, #6a11cb, #2575fc)",
                    color: "#fff",
                    border: "none",
                    boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
                    fontSize: "1rem",
                  }}
                  onClick={handleDownload}
                >
                  <FaDownload />
                </Button>
              )}
              <Button
                variant="outline-warning"
                style={{
                  borderRadius: "10px",
                  padding: "6px 16px",
                  fontWeight: "bold",
                  background: "linear-gradient(to right, #6a11cb, #2575fc)",
                  color: "#fff",
                  border: "none",
                  boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
                  fontSize: "1rem",
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                <TbReplaceFilled />
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".jpg,.png,.pdf"
                style={{ display: "none" }}
                onChange={handleFileReplace}
              />
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ textAlign: "center" }}>
          {selectedDocUrl ? (
            selectedDocUrl.endsWith(".pdf") ? (
              <iframe
                src={selectedDocUrl}
                width="100%"
                height="500px"
                style={{ border: "none" }}
                title="PDF Document Viewer"
              ></iframe>
            ) : (
              <img
                src={selectedDocUrl}
                alt="Uploaded Document"
                style={{
                  maxWidth: "100%",
                  height: "auto",
                  borderRadius: "8px",
                }}
                onError={(e) => {
                  e.target.src = "/fallback-image.png";
                }}
              />
            )
          ) : (
            <p style={{ color: "red", fontWeight: "bold" }}>
              No document available
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowViewModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={showLateApprovalModal}
        onHide={() => {
          setShowLateApprovalModal(false);
          resetApprovalData();
        }}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Late Approval Form</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div ref={printRef} className="approval-form">
            <div className="form-details">
              <p>
                <strong>Date:</strong> {approvalData.date}
              </p>
              <p>
                <strong>Employee Name:</strong> {approvalData.userName}
              </p>
              <p>
                <strong>Department:</strong> {approvalData.department}
              </p>
              <p>
                <strong>Late Minutes:</strong> {approvalData.minutes}
              </p>
              <p>
                <strong>Reason:</strong> {approvalData.reason}
              </p>
              <p>
                <strong>Single Deduction Applied:</strong>{" "}
                {approvalData.singleDeduction ? "Yes" : "No"}
              </p>
              {role === "admin" && !approvalData.adminHasApproved && (
                <Form.Check
                  type="checkbox"
                  label="Apply Single Deduction"
                  checked={applySingleDeduction}
                  onChange={(e) => setApplySingleDeduction(e.target.checked)}
                  style={{ margin: "10px 0" }}
                />
              )}
              <hr />
              <div className="signature-area mt-4">
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <div style={{ textAlign: "center", width: "45%" }}>
                    <p>
                      <strong>HR Signature</strong>
                    </p>
                    {approvalData.canEditHRSignature ? (
                      <Form.Control
                        type="text"
                        value={hrSignature}
                        onChange={(e) => setHrSignature(e.target.value)}
                        placeholder="Enter HR signature"
                        style={{ margin: "10px 0" }}
                      />
                    ) : (
                      <div
                        style={{
                          borderBottom: "1px solid #000",
                          width: "100%",
                          margin: "10px auto",
                        }}
                      >
                        {hrSignature && (
                          <p style={{ fontStyle: "italic" }}>{hrSignature}</p>
                        )}
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: "center", width: "45%" }}>
                    <p>
                      <strong>Admin Signature</strong>
                    </p>
                    {approvalData.canEditAdminSignature ? (
                      <Form.Control
                        type="text"
                        value={adminSignature}
                        onChange={(e) => setAdminSignature(e.target.value)}
                        placeholder="Enter Admin signature"
                        style={{ margin: "10px 0" }}
                      />
                    ) : (
                      <div
                        style={{
                          borderBottom: "1px solid #000",
                          width: "100%",
                          margin: "10px auto",
                        }}
                      >
                        {adminSignature && (
                          <p style={{ fontStyle: "italic" }}>
                            {adminSignature}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          {(approvalData.canEditHRSignature ||
            approvalData.canEditAdminSignature) && (
            <Button variant="success" onClick={() => handleApprove("late")}>
              {approvalData.canEditHRSignature ? "Semi-Approve" : "Approve"}
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={() => {
              setShowLateApprovalModal(false);
              resetApprovalData();
            }}
          >
            Close
          </Button>
          <Button variant="primary" onClick={handlePrint}>
            <FaPrint /> Print
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={showEarlyLeaveApprovalModal}
        onHide={() => {
          setShowEarlyLeaveApprovalModal(false);
          resetApprovalData();
        }}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Early Leave Approval Form</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div ref={printRef} className="approval-form">
            <div className="form-details">
              <p>
                <strong>Date:</strong> {approvalData.date}
              </p>
              <p>
                <strong>Employee Name:</strong> {approvalData.userName}
              </p>
              <p>
                <strong>Department:</strong> {approvalData.department}
              </p>
              <p>
                <strong>Early Leave Minutes:</strong> {approvalData.minutes}
              </p>
              <p>
                <strong>Reason:</strong> {approvalData.reason}
              </p>
              <p>
                <strong>Single Deduction Applied:</strong>{" "}
                {approvalData.singleDeduction ? "Yes" : "No"}
              </p>
              {role === "admin" && !approvalData.adminHasApproved && (
                <Form.Check
                  type="checkbox"
                  label="Apply Single Deduction"
                  checked={applySingleDeduction}
                  onChange={(e) => setApplySingleDeduction(e.target.checked)}
                  style={{ margin: "10px 0" }}
                />
              )}
              <hr />
              <div className="signature-area mt-4">
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <div style={{ textAlign: "center", width: "45%" }}>
                    <p>
                      <strong>HR Signature</strong>
                    </p>
                    {approvalData.canEditHRSignature ? (
                      <Form.Control
                        type="text"
                        value={hrSignature}
                        onChange={(e) => setHrSignature(e.target.value)}
                        placeholder="Enter HR signature"
                        style={{ margin: "10px 0" }}
                      />
                    ) : (
                      <div
                        style={{
                          borderBottom: "1px solid #000",
                          width: "100%",
                          margin: "10px auto",
                        }}
                      >
                        {hrSignature && (
                          <p style={{ fontStyle: "italic" }}>{hrSignature}</p>
                        )}
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: "center", width: "45%" }}>
                    <p>
                      <strong>Admin Signature</strong>
                    </p>
                    {approvalData.canEditAdminSignature ? (
                      <Form.Control
                        type="text"
                        value={adminSignature}
                        onChange={(e) => setAdminSignature(e.target.value)}
                        placeholder="Enter Admin signature"
                        style={{ margin: "10px 0" }}
                      />
                    ) : (
                      <div
                        style={{
                          borderBottom: "1px solid #000",
                          width: "100%",
                          margin: "10px auto",
                        }}
                      >
                        {adminSignature && (
                          <p style={{ fontStyle: "italic" }}>
                            {adminSignature}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          {(approvalData.canEditHRSignature ||
            approvalData.canEditAdminSignature) && (
            <Button
              variant="success"
              onClick={() => handleApprove("earlyClose")}
            >
              {approvalData.canEditHRSignature ? "Semi-Approve" : "Approve"}
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={() => {
              setShowEarlyLeaveApprovalModal(false);
              resetApprovalData();
            }}
          >
            Close
          </Button>
          <Button variant="primary" onClick={handlePrint}>
            <FaPrint /> Print
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={showOvertimeApprovalModal}
        onHide={() => {
          setShowOvertimeApprovalModal(false);
          resetApprovalData();
        }}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Overtime Approval Form</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div ref={printRef} className="approval-form">
            <div className="form-details">
              <p>
                <strong>Date:</strong> {approvalData.date}
              </p>
              <p>
                <strong>Employee Name:</strong> {approvalData.userName}
              </p>
              <p>
                <strong>Department:</strong> {approvalData.department}
              </p>
              <p>
                <strong>Overtime Minutes:</strong> {approvalData.minutes}
              </p>
              <p>
                <strong>Reason:</strong> {approvalData.reason}
              </p>
              <hr />
              <div className="signature-area mt-4">
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <div style={{ textAlign: "center", width: "45%" }}>
                    <p>
                      <strong>HR Signature</strong>
                    </p>
                    {approvalData.canEditHRSignature ? (
                      <Form.Control
                        type="text"
                        value={hrSignature}
                        onChange={(e) => setHrSignature(e.target.value)}
                        placeholder="Enter HR signature"
                        style={{ margin: "10px 0" }}
                      />
                    ) : (
                      <div
                        style={{
                          borderBottom: "1px solid #000",
                          width: "100%",
                          margin: "10px auto",
                        }}
                      >
                        {hrSignature && (
                          <p style={{ fontStyle: "italic" }}>{hrSignature}</p>
                        )}
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: "center", width: "45%" }}>
                    <p>
                      <strong>Admin Signature</strong>
                    </p>
                    {approvalData.canEditAdminSignature ? (
                      <Form.Control
                        type="text"
                        value={adminSignature}
                        onChange={(e) => setAdminSignature(e.target.value)}
                        placeholder="Enter Admin signature"
                        style={{ margin: "10px 0" }}
                      />
                    ) : (
                      <div
                        style={{
                          borderBottom: "1px solid #000",
                          width: "100%",
                          margin: "10px auto",
                        }}
                      >
                        {adminSignature && (
                          <p style={{ fontStyle: "italic" }}>
                            {adminSignature}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          {(approvalData.canEditHRSignature ||
            approvalData.canEditAdminSignature) && (
            <Button variant="success" onClick={() => handleApprove("overtime")}>
              {approvalData.canEditHRSignature ? "Semi-Approve" : "Approve"}
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={() => {
              setShowOvertimeApprovalModal(false);
              resetApprovalData();
            }}
          >
            Close
          </Button>
          <Button variant="primary" onClick={handlePrint}>
            <FaPrint /> Print
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false);
          setItemToDelete(null);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the attendance record for{" "}
          {itemToDelete?.userName} on{" "}
          {itemToDelete?.checkIn
            ? new Date(itemToDelete.checkIn).toLocaleDateString()
            : "N/A"}
          🤔?
          <br />
          <strong>This action cannot be undone.</strong>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowDeleteModal(false);
              setItemToDelete(null);
            }}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </AtdLayout>
  );
}
